import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { RefreshCw, X, Plus, LogOut, Pencil } from "lucide-react";
import { toast } from "sonner";

const pnlCls = (v) => (v > 0 ? "text-[#22C55E]" : v < 0 ? "text-[#EF4444]" : "text-zinc-400");
const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : v ?? "—");
const ORDER_TYPES = [
  { value: "MARKET", label: "MARKET" },
  { value: "LIMIT", label: "LIMIT" },
  { value: "STOP_LOSS", label: "SL (trigger + limit)" },
  { value: "STOP_LOSS_MARKET", label: "SL-M (trigger)" },
];
const needsPrice = (t) => t === "LIMIT" || t === "STOP_LOSS";
const needsTrigger = (t) => t === "STOP_LOSS" || t === "STOP_LOSS_MARKET";
const typeShort = (t) => (t === "STOP_LOSS" ? "SL" : t === "STOP_LOSS_MARKET" ? "SL-M" : t);
const isMarketType = (t) => t === "MARKET" || t === "STOP_LOSS_MARKET";

/** SEBI CAS guardrail — after 15:15 IST the backend rejects MARKET / SL-M; UI mirrors it. */
export function useCasSession() {
  const [cas, setCas] = useState({ cas_limit_only: false, cas_from: "15:15" });
  useEffect(() => {
    let alive = true;
    const load = async () => { try { const { data } = await api.get("/market/session"); if (alive) setCas(data); } catch (e) { console.warn("session poll failed:", e?.message || e); } };
    load(); const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return cas;
}

const livePnl = (p, ltpVal) => {
  if (ltpVal != null && (p.net_qty ?? 0) !== 0) {
    const unreal = p.net_qty > 0
      ? (ltpVal - (p.buy_avg ?? p.cost_price ?? 0)) * p.net_qty
      : ((p.sell_avg ?? p.cost_price ?? 0) - ltpVal) * Math.abs(p.net_qty);
    return (p.realized_pnl ?? 0) + unreal;
  }
  return (p.unrealized_pnl ?? 0) + (p.realized_pnl ?? 0);
};

export function OrderTypeFields({ type, setType, price, setPrice, trigger, setTrigger, idPrefix, limitOnly = false }) {
  useEffect(() => { if (limitOnly && isMarketType(type)) setType(type === "STOP_LOSS_MARKET" ? "STOP_LOSS" : "LIMIT"); }, [limitOnly, type, setType]);
  const types = limitOnly ? ORDER_TYPES.filter((o) => !isMarketType(o.value)) : ORDER_TYPES;
  return (
    <>
      <select value={type} onChange={(e) => setType(e.target.value)}
              className="bg-[#121212] border border-zinc-700 px-1 py-0.5 text-[10px] font-mono" data-testid={`${idPrefix}-type`}>
        {types.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {needsPrice(type) && (
        <input type="number" step="0.05" placeholder="price" value={price} onChange={(e) => setPrice(e.target.value)}
               className="w-20 bg-transparent border border-zinc-700 px-1.5 py-0.5 text-[11px] font-mono" data-testid={`${idPrefix}-price`} />
      )}
      {needsTrigger(type) && (
        <input type="number" step="0.05" placeholder="trigger" value={trigger} onChange={(e) => setTrigger(e.target.value)}
               className="w-20 bg-transparent border border-[#F59E0B]/50 px-1.5 py-0.5 text-[11px] font-mono" data-testid={`${idPrefix}-trigger`} />
      )}
    </>
  );
}

export function validateOrder(type, price, trigger) {
  if (needsPrice(type) && !(Number(price) > 0)) return "Enter a price";
  if (needsTrigger(type) && !(Number(trigger) > 0)) return "Enter a trigger price";
  return "";
}

function TicketForm({ pos, ltp, mode, onDone, limitOnly }) {
  const isExit = mode === "exit";
  const idp = isExit ? "tt-exit" : "tt-add";
  const [qty, setQty] = useState(Math.abs(pos.net_qty) || 1);
  const [type, setType] = useState(limitOnly ? "LIMIT" : "MARKET");
  const [price, setPrice] = useState("");
  const [trigger, setTrigger] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const err = validateOrder(type, price, trigger);
    if (err) { toast.error(err); return; }
    if (isExit && Number(qty) > Math.abs(pos.net_qty)) { toast.error("Exit qty exceeds position size"); return; }
    setBusy(true);
    try {
      const body = isExit
        ? { security_id: pos.security_id, exchange_segment: pos.exchange_segment,
            net_qty: pos.net_qty, qty_units: Number(qty), order_type: type,
            price: Number(price) || 0, trigger_price: Number(trigger) || 0, confirm: true }
        : { security_id: pos.security_id, exchange_segment: pos.exchange_segment,
            qty_units: Number(qty), order_type: type, price: Number(price) || 0,
            trigger_price: Number(trigger) || 0,
            side: pos.net_qty >= 0 ? "BUY" : "SELL", confirm: true };
      const { data } = await api.post(isExit ? "/dhan/positions/exit" : "/dhan/positions/add", body);
      if (data.ok) toast.success(`${isExit ? "Exit order" : "Order"} placed · ${data.order_status || data.order_id}`);
      else toast.error(data.detail || "Order rejected");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${idp}-form-${pos.security_id}`}>
      <span className={`text-[9px] uppercase tracking-widest font-mono ${isExit ? "text-[#EF4444]" : "text-[#3B9BFF]"}`}>
        {isExit ? "Exit:" : "Add:"}
      </span>
      {ltp != null && (
        <button type="button" onClick={() => { setPrice(ltp); if (needsTrigger(type)) setTrigger(ltp); }}
                title="Click to use LTP as price"
                className="px-1.5 py-0.5 border border-zinc-700 hover:border-[#007AFF] text-[10px] font-mono text-zinc-300"
                data-testid={`${idp}-ltp`}>
          LTP ₹{fmt(ltp)}
        </button>
      )}
      <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
             className="w-16 bg-transparent border border-zinc-700 px-1.5 py-0.5 text-[11px] font-mono" data-testid={`${idp}-qty`} />
      <OrderTypeFields type={type} setType={setType} price={price} setPrice={setPrice}
                       trigger={trigger} setTrigger={setTrigger} idPrefix={idp} limitOnly={limitOnly} />
      <button onClick={submit} disabled={busy}
              className={`px-2 py-0.5 text-[10px] font-bold uppercase disabled:opacity-50 ${isExit ? "bg-[#EF4444] hover:bg-[#F87171]" : "bg-[#007AFF] hover:bg-[#3395FF]"}`}
              data-testid={`${idp}-submit`}>
        {busy ? "…" : isExit ? "Place Exit" : "Place"}
      </button>
    </div>
  );
}

function ModifyForm({ o, onDone, limitOnly }) {
  const [qty, setQty] = useState(o.qty || 1);
  const [type, setType] = useState(o.order_type || "LIMIT");
  const [price, setPrice] = useState(o.price || "");
  const [trigger, setTrigger] = useState(o.trigger_price || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const err = validateOrder(type, price, trigger);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/dhan/orders/${o.order_id}/modify`, {
        order_type: type, quantity: Number(qty),
        price: Number(price) || 0, trigger_price: Number(trigger) || 0, confirm: true,
      });
      if (data.ok) toast.success(`Order modified · ${data.order_status || data.order_id}`);
      else toast.error(data.detail || "Modify rejected");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modify failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`tt-modify-form-${o.order_id}`}>
      <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Modify:</span>
      <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
             className="w-16 bg-transparent border border-zinc-700 px-1.5 py-0.5 text-[11px] font-mono" data-testid="tt-modify-qty" />
      <OrderTypeFields type={type} setType={setType} price={price} setPrice={setPrice}
                       trigger={trigger} setTrigger={setTrigger} idPrefix="tt-modify" limitOnly={limitOnly} />
      <button onClick={submit} disabled={busy}
              className="px-2 py-0.5 bg-[#F59E0B] hover:bg-[#FBbf24] text-black text-[10px] font-bold uppercase disabled:opacity-50" data-testid="tt-modify-submit">
        {busy ? "…" : "Update"}
      </button>
    </div>
  );
}

function PositionRow({ p, ltp, tick, onAction, limitOnly }) {
  const [mode, setMode] = useState(null);
  const ltpVal = ltp?.[String(p.security_id)];
  const pnl = livePnl(p, ltpVal);
  const open = (p.net_qty ?? 0) !== 0;
  return (
    <>
      <tr className="border-b border-zinc-900/60 hover:bg-zinc-900/30" data-testid={`tt-pos-${p.security_id}`}>
        <td className="py-1.5 pr-2">
          <div className="text-[11px] text-zinc-200 font-medium">{p.symbol}</div>
          <div className="text-[9px] text-zinc-600 font-mono uppercase">{p.product} · {p.position_type}</div>
        </td>
        <td className={`text-right font-mono text-[11px] ${p.net_qty > 0 ? "text-[#22C55E]" : p.net_qty < 0 ? "text-[#EF4444]" : "text-zinc-500"}`}>{p.net_qty}</td>
        <td className="text-right font-mono text-[11px] text-zinc-300">₹{fmt(p.buy_avg ?? p.cost_price)}</td>
        <td className={`text-right font-mono text-[11px] font-bold transition-colors duration-300 ${tick === "up" ? "text-[#22C55E]" : tick === "down" ? "text-[#EF4444]" : "text-zinc-200"}`}
            data-testid={`tt-ltp-${p.security_id}`}>
          {ltpVal != null ? `₹${fmt(ltpVal)}` : "—"}
        </td>
        <td className={`text-right font-mono text-[11px] font-bold ${pnlCls(pnl)}`} data-testid={`tt-pnl-${p.security_id}`}>
          {pnl >= 0 ? "+" : ""}₹{fmt(pnl)}
        </td>
        <td className="text-right whitespace-nowrap">
          {open && (
            <>
              <button onClick={() => setMode((m) => (m === "add" ? null : "add"))} title="Add quantity"
                      className="px-1.5 py-0.5 border border-zinc-700 hover:border-[#007AFF] text-[9px] font-mono uppercase text-[#3B9BFF] mr-1"
                      data-testid={`tt-add-${p.security_id}`}>
                <Plus className="h-3 w-3 inline" /> Add
              </button>
              <button onClick={() => setMode((m) => (m === "exit" ? null : "exit"))} title="Square off — market, limit or stop"
                      className="px-1.5 py-0.5 border border-[#EF4444]/50 hover:bg-[#EF4444]/10 text-[9px] font-mono uppercase text-[#EF4444]"
                      data-testid={`tt-exit-${p.security_id}`}>
                <LogOut className="h-3 w-3 inline" /> Exit
              </button>
            </>
          )}
          {!open && <span className="text-[9px] text-zinc-600 font-mono uppercase">closed</span>}
        </td>
      </tr>
      {mode && open && (
        <tr><td colSpan={6} className="py-1.5 pl-2 bg-zinc-900/20"><TicketForm pos={p} ltp={ltpVal} mode={mode} limitOnly={limitOnly} onDone={() => { setMode(null); onAction(); }} /></td></tr>
      )}
    </>
  );
}

function OrderRow({ o, onAction, limitOnly }) {
  const [editing, setEditing] = useState(false);
  const pending = ["PENDING", "TRANSIT", "TRIGGER_PENDING"].includes((o.status || "").toUpperCase());
  const rejected = (o.status || "").toUpperCase() === "REJECTED";
  const cancel = async () => {
    try {
      const { data } = await api.post(`/dhan/orders/${o.order_id}/cancel`);
      data.ok ? toast.success("Order cancelled") : toast.error(data.detail || "Cancel failed");
      onAction();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  return (
    <>
    <tr className="border-b border-zinc-900/60" data-testid={`tt-order-${o.order_id}`}>
      <td className="py-1.5 pr-2">
        <div className="text-[11px] text-zinc-200">{o.symbol}</div>
        <div className="text-[9px] text-zinc-600 font-mono">{o.time}</div>
      </td>
      <td className={`text-[10px] font-mono ${o.side === "BUY" ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{o.side} {typeShort(o.order_type)}</td>
      <td className="text-right font-mono text-[11px] text-zinc-300">{o.filled_qty ?? 0}/{o.qty}</td>
      <td className="text-right font-mono text-[11px] text-zinc-300">
        {o.order_type === "MARKET" ? "MKT" : o.order_type === "STOP_LOSS_MARKET" ? "MKT" : `₹${fmt(o.price)}`}
        {(o.trigger_price ?? 0) > 0 && (
          <div className="text-[9px] text-[#F59E0B] font-mono" data-testid={`tt-trigger-${o.order_id}`}>trg ₹{fmt(o.trigger_price)}</div>
        )}
      </td>
      <td className="text-right">
        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${rejected ? "border-[#EF4444]/50 text-[#EF4444]" : pending ? "border-[#F59E0B]/50 text-[#F59E0B]" : "border-[#22C55E]/40 text-[#22C55E]"}`}>
          {o.status}
        </span>
        {pending && (
          <>
            <button onClick={() => setEditing((v) => !v)} className="ml-1 px-1 py-0.5 border border-zinc-700 hover:border-[#F59E0B] text-zinc-500 hover:text-[#F59E0B]"
                    title="Modify order" data-testid={`tt-modify-${o.order_id}`}>
              <Pencil className="h-3 w-3 inline" />
            </button>
            <button onClick={cancel} className="ml-1 px-1 py-0.5 border border-zinc-700 hover:border-[#EF4444] text-zinc-500 hover:text-[#EF4444]"
                    title="Cancel order" data-testid={`tt-cancel-${o.order_id}`}>
              <X className="h-3 w-3 inline" />
            </button>
          </>
        )}
        {rejected && o.reject_reason && (
          <div className="text-[9px] text-[#EF4444]/80 mt-0.5 max-w-[260px] truncate" title={o.reject_reason} data-testid={`tt-reject-${o.order_id}`}>
            {o.reject_reason}
          </div>
        )}
      </td>
    </tr>
    {editing && pending && (
      <tr><td colSpan={5} className="py-1.5 pl-2 bg-zinc-900/20"><ModifyForm o={o} limitOnly={limitOnly} onDone={() => { setEditing(false); onAction(); }} /></td></tr>
    )}
    </>
  );
}

/** Live trading terminal — positions + order book with ADD / EXIT / CANCEL (DHAN-style). */
export default function TradeTerminal() {
  const [tab, setTab] = useState("positions");
  const [pos, setPos] = useState(null);
  const [orders, setOrders] = useState(null);
  const [ltp, setLtp] = useState({});
  const [ticks, setTicks] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [wsLive, setWsLive] = useState(false);
  const [feedState, setFeedState] = useState("off");
  const [tickAge, setTickAge] = useState(null);
  const posRef = useRef(null);
  const ltpRef = useRef({});
  const wsRef = useRef(null);
  const wsLiveRef = useRef(false);
  const lastPosTickRef = useRef(0);

  const sendSubs = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    const open = (posRef.current || []).filter((p) => (p.net_qty ?? 0) !== 0);
    ws.send(JSON.stringify({
      type: "subscribe",
      instruments: open.map((p) => ({ segment: p.exchange_segment || "NSE_FNO", security_id: String(p.security_id) })),
    }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([api.get("/dhan/positions"), api.get("/dhan/orders")]);
      if (p.data.ok) { setPos(p.data.items); posRef.current = p.data.items; setErr(""); sendSubs(); } else setErr(p.data.detail || "Broker unavailable");
      if (o.data.ok) setOrders(o.data.items);
    } catch (e) {
      console.warn("terminal load failed:", e?.message || e);
    } finally { setLoading(false); }
  }, []);

  const loadLtp = useCallback(async () => {
    const open = (posRef.current || []).filter((p) => (p.net_qty ?? 0) !== 0);
    if (!open.length) return;
    const segments = {};
    open.forEach((p) => {
      const seg = p.exchange_segment || "NSE_FNO";
      (segments[seg] = segments[seg] || []).push(Number(p.security_id));
    });
    try {
      const { data } = await api.post("/dhan/ltp", { segments });
      if (!data.ok) return;
      const flat = {};
      Object.values(data.ltp || {}).forEach((rows) => Object.entries(rows || {}).forEach(([sid, v]) => { if (v != null) flat[sid] = v; }));
      const prev = ltpRef.current;
      const t = {};
      Object.entries(flat).forEach(([sid, v]) => {
        if (prev[sid] != null && v !== prev[sid]) t[sid] = v > prev[sid] ? "up" : "down";
      });
      ltpRef.current = flat;
      setLtp(flat);
      setTicks(t);
    } catch (e) { console.warn("ltp poll failed:", e?.message || e); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    loadLtp();
    // REST safety net: poll when the socket is not live OR it claims live but no
    // position tick has arrived in the last 5s (silent DHAN stall).
    const id = setInterval(() => {
      const stale = Date.now() - lastPosTickRef.current > 5000;
      if (!wsLiveRef.current || stale) loadLtp();
    }, 3000);
    return () => clearInterval(id);
  }, [loadLtp]);

  useEffect(() => {
    const id = setInterval(() => {
      setTickAge(lastPosTickRef.current ? Math.round((Date.now() - lastPosTickRef.current) / 1000) : null);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // ---- live tick websocket (admin feed) with auto-reconnect; REST polling covers gaps
  useEffect(() => {
    let closed = false;
    let retry = null;
    const connect = () => {
      if (closed) return;
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      let ws;
      try { ws = new WebSocket(`${base}/api/ws/terminal`); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => sendSubs();
      ws.onmessage = (ev) => {
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === "tick") {
          if (m.segment === "IDX_I") return; // index spots not shown in terminal rows
          const sid = String(m.security_id);
          lastPosTickRef.current = Date.now();
          const prev = ltpRef.current[sid];
          ltpRef.current = { ...ltpRef.current, [sid]: m.ltp };
          setLtp((old) => ({ ...old, [sid]: m.ltp }));
          if (prev != null && prev !== m.ltp) setTicks((old) => ({ ...old, [sid]: m.ltp > prev ? "up" : "down" }));
        } else if (m.type === "order") {
          const label = `${m.txn || ""} ${m.symbol || "order"}`.trim();
          if (m.status === "TRADED") toast.success(`FILLED — ${label}${m.avg_price ? ` @ ₹${m.avg_price}` : ""}`);
          else if (m.status === "REJECTED") toast.error(`REJECTED — ${label}${m.reason ? `: ${m.reason}` : ""}`);
          else if (m.status === "CANCELLED") toast(`Cancelled — ${label}`);
          else if (m.status === "PART_TRADED") toast(`Partial fill — ${label}`);
          load();
        } else if (m.type === "feed_status" || m.type === "hello") {
          const st = m.feed || m.status?.feed || "off";
          const live = st === "live";
          setFeedState(st);
          setWsLive(live);
          wsLiveRef.current = live;
        }
      };
      ws.onclose = () => {
        setWsLive(false);
        setFeedState("off");
        wsLiveRef.current = false;
        if (!closed) retry = setTimeout(connect, 4000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); try { wsRef.current?.close(); } catch {} };
  }, [sendSubs, load]);

  const cas = useCasSession();
  const limitOnly = !!cas.cas_limit_only;
  const totalPnl = (pos || []).reduce((s, p) => s + livePnl(p, ltp[String(p.security_id)]), 0);
  const openCount = (pos || []).filter((p) => (p.net_qty ?? 0) !== 0).length;

  return (
    <section className="bg-[#121212] border border-zinc-800" data-testid="trade-terminal">
      <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">◢ Trade Terminal</span>
          {limitOnly && (
            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#F59E0B] text-[#F59E0B]"
                  title="SEBI CAS guardrail: after 3:15 PM IST market orders are blocked — closing-auction prices spike unpredictably. LIMIT / SL only."
                  data-testid="tt-cas-limit-only">CAS · LIMIT ONLY after {cas.cas_from}</span>
          )}
          {wsLive && (
            <span className="flex items-center gap-1 text-[9px] font-mono text-[#22C55E]" data-testid="tt-live-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] animate-pulse" />TICK LIVE
              {openCount > 0 && tickAge != null && (
                <span className={tickAge > 5 ? "text-[#F59E0B]" : "text-zinc-500"} data-testid="tt-tick-age">· {tickAge}s ago</span>
              )}
            </span>
          )}
          {!wsLive && feedState === "auth_failed" && (
            <span className="text-[9px] font-mono text-[#EF4444]" title="DHAN rejected the live feed — token expired/invalid. Falling back to REST polling." data-testid="tt-feed-auth-failed">
              FEED: TOKEN INVALID
            </span>
          )}
          {!wsLive && (feedState === "reconnecting" || feedState === "connecting") && (
            <span className="text-[9px] font-mono text-[#F59E0B]" data-testid="tt-feed-reconnecting">FEED {feedState.toUpperCase()}</span>
          )}
          <button onClick={() => setTab("positions")}
                  className={`text-[10px] uppercase tracking-widest font-bold pb-0.5 border-b-2 ${tab === "positions" ? "border-[#007AFF] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
                  data-testid="tt-tab-positions">
            Positions{openCount ? ` (${openCount})` : ""}
          </button>
          <button onClick={() => setTab("orders")}
                  className={`text-[10px] uppercase tracking-widest font-bold pb-0.5 border-b-2 ${tab === "orders" ? "border-[#007AFF] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
                  data-testid="tt-tab-orders">
            Orders{orders?.length ? ` (${orders.length})` : ""}
          </button>
        </div>
        <div className="flex items-center gap-2">
          {pos && pos.length > 0 && (
            <span className={`text-[11px] font-mono font-bold ${pnlCls(totalPnl)}`} data-testid="tt-total-pnl">
              Day P&L: {totalPnl >= 0 ? "+" : ""}₹{fmt(totalPnl)}
            </span>
          )}
          <button onClick={load} className="text-zinc-500 hover:text-white p-1" data-testid="tt-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="px-3 py-2 overflow-x-auto">
        {err && <div className="text-[10px] font-mono text-zinc-500 py-3 text-center" data-testid="tt-error">{err}</div>}
        {!err && tab === "positions" && (
          pos === null ? <div className="text-[10px] font-mono text-zinc-600 py-3 text-center">◢ loading…</div> :
          pos.length === 0 ? <div className="text-[10px] font-mono text-zinc-600 py-3 text-center" data-testid="tt-no-positions">No positions today</div> :
          <div className="overflow-x-auto"><table className="w-full text-left min-w-[560px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono border-b border-zinc-800">
                <th className="py-1 font-normal">Instrument</th>
                <th className="py-1 font-normal text-right">Qty</th>
                <th className="py-1 font-normal text-right">Avg</th>
                <th className="py-1 font-normal text-right">LTP</th>
                <th className="py-1 font-normal text-right">P&L</th>
                <th className="py-1 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>{pos.map((p) => <PositionRow key={`${p.security_id}-${p.product}`} p={p} ltp={ltp} tick={ticks[String(p.security_id)]} onAction={load} limitOnly={limitOnly} />)}</tbody>
          </table></div>
        )}
        {!err && tab === "orders" && (
          orders === null ? <div className="text-[10px] font-mono text-zinc-600 py-3 text-center">◢ loading…</div> :
          orders.length === 0 ? <div className="text-[10px] font-mono text-zinc-600 py-3 text-center" data-testid="tt-no-orders">No orders today</div> :
          <div className="overflow-x-auto"><table className="w-full text-left min-w-[560px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono border-b border-zinc-800">
                <th className="py-1 font-normal">Instrument</th>
                <th className="py-1 font-normal">Type</th>
                <th className="py-1 font-normal text-right">Filled</th>
                <th className="py-1 font-normal text-right">Price</th>
                <th className="py-1 font-normal text-right">Status</th>
              </tr>
            </thead>
            <tbody>{orders.map((o) => <OrderRow key={o.order_id} o={o} onAction={load} limitOnly={limitOnly} />)}</tbody>
          </table></div>
        )}
      </div>
    </section>
  );
}
